import type { ReleaseManifest } from './release-manifest.js'

/** t1183: what the Android setup wizard needs to turn a factory-reset tablet
 *  into a device-owner kiosk from a scanned QR code. The package and receiver
 *  names mirror the app's applicationId and `KioskDeviceAdminReceiver` in the
 *  vending-android repo. */
export const VENDING_PACKAGE = 'tech.mevaser.vending'
export const VENDING_ADMIN_COMPONENT = `${VENDING_PACKAGE}/${VENDING_PACKAGE}.kiosk.KioskDeviceAdminReceiver`

/** First build that answers the setup wizard's provisioning-mode and
 *  policy-compliance calls (t1183). On anything older, Android 12+ downloads
 *  the app and then aborts the enrolment, so the QR is withheld below this. */
export const MIN_QR_ENROLLMENT_VERSION_CODE = 26

export type WifiSecurity = 'WPA' | 'WEP' | 'NONE'

export interface ProvisioningWifi {
  ssid: string
  password?: string
  security: WifiSecurity
}

const EXTRA = 'android.app.extra.PROVISIONING_'

/** Android wants the APK checksum as URL-safe base64 without padding, not the
 *  hex the release manifest publishes. */
export function sha256HexToBase64Url(hex: string): string {
  if (!/^[0-9a-fA-F]{64}$/.test(hex)) throw new Error('Expected a 64-character sha256 hex digest')
  let binary = ''
  for (let i = 0; i < 64; i += 2)
    binary += String.fromCharCode(Number.parseInt(hex.slice(i, i + 2), 16))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** The JSON the QR code carries. The download location is the immutable
 *  per-build URL, so a printed QR keeps working after later releases — the
 *  tablet then updates itself over the air as usual. */
export function buildProvisioningPayload(
  manifest: Pick<ReleaseManifest, 'url' | 'sha256'>,
  opts: { wifi?: ProvisioningWifi } = {},
): Record<string, string | boolean> {
  const payload: Record<string, string | boolean> = {
    [`${EXTRA}DEVICE_ADMIN_COMPONENT_NAME`]: VENDING_ADMIN_COMPONENT,
    [`${EXTRA}DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION`]: manifest.url,
    [`${EXTRA}DEVICE_ADMIN_PACKAGE_CHECKSUM`]: sha256HexToBase64Url(manifest.sha256),
    // Without this, fully managed setup disables the system apps (keyboard,
    // camera, settings) the kiosk and its staff screens rely on.
    [`${EXTRA}LEAVE_ALL_SYSTEM_APPS_ENABLED`]: true,
  }
  const wifi = opts.wifi
  if (wifi && wifi.ssid.trim()) {
    payload[`${EXTRA}WIFI_SSID`] = wifi.ssid.trim()
    payload[`${EXTRA}WIFI_SECURITY_TYPE`] = wifi.security
    if (wifi.security !== 'NONE' && wifi.password) payload[`${EXTRA}WIFI_PASSWORD`] = wifi.password
  }
  return payload
}

/** Prints the enrolment QR on its own page, through the same hidden-iframe
 *  route the label printer uses (see label-print.ts for why not a popup). */
export function printEnrollmentQr(qrDataUrl: string): void {
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  document.body.appendChild(iframe)
  const cleanup = () => iframe.remove()

  const doc = iframe.contentDocument
  if (!doc) {
    cleanup()
    return
  }
  doc.open()
  doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<style>
  body { margin: 0; padding: 0.5in; font-family: system-ui, sans-serif; text-align: center; }
  img { width: 4in; height: 4in; }
  h1 { font-size: 20px; margin: 0 0 8px; }
  p { font-size: 13px; margin: 4px 0; }
</style>
</head>
<body>
  <h1>Mevaser kiosk tablet setup</h1>
  <p>Factory reset the tablet, tap the welcome screen 6 times, then scan this code.</p>
  <img src="${qrDataUrl}" alt="" />
</body>
</html>`)
  doc.close()

  iframe.contentWindow?.addEventListener('afterprint', cleanup)
  setTimeout(cleanup, 60_000)
  requestAnimationFrame(() => {
    iframe.contentWindow?.focus()
    iframe.contentWindow?.print()
  })
}
